import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { BLOG_NAV_HEIGHT } from '@/components/navigation/BlogNavigation';
import ShareButtons from '@/components/ShareButtons';
import VizyBlogArticle from '@/components/vizy/VizyBlogArticle';
import { FRONT_PAGE_HOME } from '@/constants/routes';
import { GlobalContext } from '@/context';
import { BlogArticlePathsQuery, BlogArticleQuery, BlogArticleQueryVariables } from '@/graphql';
import BlogArticleQueryDocument from '@/graphql/queries/BlogArticle.gql';
import BlogArticlePathsQueryDocument from '@/graphql/queries/BlogArticlePaths.gql';
import CraftClient from '@/lib/graphql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { COLORS } from '@/styles/color';
import { getBrandColor } from '@/styles/color/utils';
import { marginX, marginY, mediaBreakpointDown, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import BlogTemplate from '@/templates/BlogTemplate';
import dayjs from 'dayjs';
import { useContext, useMemo, useRef } from 'react';
import styled from 'styled-components';

// #region - Components

interface ArticleWrapperProps {
  brandColor: string;
}

const ArticleWrapper = styled.div<ArticleWrapperProps>`
  --article-accent-color-base: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'base')};
  --article-accent-color-highlight: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'highlight')};
  --article-accent-color-tint: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'tint')};
`;

const Hero = styled.header`
  ${paddingY(BLOG_NAV_HEIGHT, 32)}
  background-color: var(--article-accent-color-tint);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${remCalc(24)};
`;

const BackLink = styled.a`
  ${customTextSize(13, 21)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  display: inline-block;
  border-bottom: 2px solid;
  ${paddingX(2)}

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(17, 27)};
  }
`;

const ArticleTitle = styled.h1`
  ${customTextSize(38, 42)};
  font-weight: ${FONT_WEIGHTS.MEDIUM};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(80, 88)};
  }
`;

const CoverImageWrapper = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 60%;
    background-color: var(--article-accent-color-tint);
    z-index: -1;
  }
`;

const CoverImage = styled.div`
  position: relative;
  border-radius: ${remCalc(10)};
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
  }
`;

const AuthorBlock = styled.div`
  ${marginY(24)}
  display: flex;
  flex-direction: column;

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: center;
    grid-row: 2;
    column-gap: ${remCalc(20)};
    ${marginY(0)};
  }
`;

const AuthorPhoto = styled.div`
  position: relative;
  width: ${remCalc(57)};
  height: ${remCalc(57)};
  border-radius: 100%;
  background-color: var(--article-accent-color-highlight);
  overflow: hidden;
  margin-bottom: ${remCalc(12)};

  img {
    object-fit: cover;
    object-position: center;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 0;
  }
`;

const AuthorName = styled.p`
  text-transform: uppercase;
  ${customTextSize(15, 21)};
  letter-spacing: 0.04em;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(2)};
`;

const AuthorTitle = styled.p`
  ${customTextSize(12, 18)};
  margin-bottom: ${remCalc(2)};
`;

const ArticleMetadata = styled.p`
  ${customTextSize(12, 18)};
  color: ${COLORS.GRAY.BASE};
`;

const ArticleSummary = styled.p`
  ${customTextSize(22, 30)};
  max-width: ${remCalc(960)};
  margin: 0 auto ${remCalc(36)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(26, 37)};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    grid-row: 1;
    grid-column: 1 / -1;
    ${marginY(0)}
  }
`;

const Article = styled.article`
  max-width: ${remCalc(960)};
  ${marginX('auto')};
  width: 100%;

  ${mediaBreakpointDown('tablet')} {
    padding-bottom: ${remCalc(60)};
  }
`;

const ArticleAuthorRow = styled.div`
  grid-column: 1 / -1;

  & > div {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    max-width: ${remCalc(960)};
    margin: 0 auto;
  }
`;

interface ArticleGridProps {
  hasSummary: boolean;
}

const ArticleGrid = styled.div<ArticleGridProps>`
  display: grid;
  grid-template-columns: 100%;

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: minmax(${remCalc(500)}, ${remCalc(700)}) minmax(${remCalc(280)}, ${remCalc(370)});
    grid-template-rows: repeat(${(props) => (props.hasSummary ? 3 : 2)}, auto);
    column-gap: ${remCalc(40)};
    justify-content: space-between;
    row-gap: ${remCalc(80)};
    margin-top: ${remCalc(40)};

    ${Article} {
      grid-row: ${(props) => (props.hasSummary ? 3 : 2)};
      grid-column: 1 / -1;
    }
  }
`;

const ArticleFooter = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${remCalc(60)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

// #endregion - Components

interface PageProps extends BlogArticleQuery { }

const BlogArticle: PageTemplate<PageProps> = ({ entry }) => {
  const { previousRouterPath } = useContext(GlobalContext);
  const articleRef = useRef<HTMLElement>(null);
  const [, activeBreakpoints] = useBreakpoint();

  /* Calculate estimated read time based on 225 words per minute */
  const readTime = useMemo(
    () => Math.ceil((articleRef.current?.textContent?.split(' ').length || 0) / 225),
    [articleRef.current],
  );

  const shareLinks = (size: number) => (
    <ShareButtons
      text={entry?.title!}
      url={entry?.url!}
      email={`Hey there,\n\nI enjoyed this article from Front Page and thought you might like it too. Let me know what you think!\n\n${entry?.url}`}
      css={{ '--share-button-size': remCalc(size) }}
    />
  );

  const coverImage = entry?.coverImage?.[0];
  const author = entry?.contentAuthor?.[0];
  const authorPhoto = author?.authorPhoto?.[0];

  return (
    <ArticleWrapper brandColor={entry?.brandColor?.label!}>
      <Hero>
        <Container>
          <Toolbar>
            <Link href={previousRouterPath ?? FRONT_PAGE_HOME}>
              <BackLink
                onClick={() => {
                  gtm.track('click', {
                    element_label: 'Back',
                    location: 'Hero',
                    element_type: 'link',
                  });
                }}
              >
                &#60; Back
              </BackLink>
            </Link>
            {!activeBreakpoints.includes('desktop') && shareLinks(28)}
          </Toolbar>
          <ArticleTitle>{entry?.title}</ArticleTitle>
        </Container>
      </Hero>

      {coverImage && (
        <CoverImageWrapper>
          <Container>
            <CoverImage>
              <Image src={coverImage.url!} alt="" width={coverImage.width!} height={coverImage.height!} />
            </CoverImage>
          </Container>
        </CoverImageWrapper>
      )}

      <Container>
        <ArticleGrid hasSummary={!!entry?.summary}>
          <ArticleAuthorRow>
            <div>
              {author && (
                <AuthorBlock>
                  {authorPhoto && (
                    <AuthorPhoto>
                      <Image src={authorPhoto?.url!} alt={author.authorName ?? ''} fill />
                    </AuthorPhoto>
                  )}

                  <div>
                    <AuthorName>
                      {author.authorName}
                      {author.authorTitle && ','}
                    </AuthorName>

                    {author.authorTitle && <AuthorTitle>{author.authorTitle}</AuthorTitle>}

                    <ArticleMetadata>
                      <span>{dayjs(entry?.postDate).format(`D MMMM YYYY`)}</span> • {readTime} min read
                    </ArticleMetadata>
                  </div>
                </AuthorBlock>
              )}

              {activeBreakpoints.includes('desktop') && shareLinks(36)}
            </div>
          </ArticleAuthorRow>

          {entry?.summary && <ArticleSummary>{entry?.summary}</ArticleSummary>}

          <Article ref={articleRef}>
            <VizyBlogArticle nodes={entry?.blogArticleContent?.nodes} rawNodes={entry?.blogArticleContent?.rawNodes} />

            {(author?.authorName || entry?.originalPublicationDate) && (
              <ArticleFooter>
                {author?.authorName && <p>Written by {author?.authorName}</p>}

                {entry?.originalPublicationDate && (
                  <p>
                    <em>Originally Published: {dayjs(entry?.originalPublicationDate).format(`D MMMM YYYY`)}</em>
                  </p>
                )}
              </ArticleFooter>
            )}
          </Article>
        </ArticleGrid>
      </Container>
    </ArticleWrapper>
  );
};

BlogArticle.template = BlogTemplate;

export default BlogArticle;

export const getStaticPaths = getStaticPagePaths('blog', async (context) => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const client = new CraftClient();

  const data = await client.request<BlogArticlePathsQuery>(BlogArticlePathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<PageProps, { slug: string }>(
  (params) => `blog/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const data = await client.request<BlogArticleQuery, BlogArticleQueryVariables>(BlogArticleQueryDocument, {
      slug: params?.slug!,
    });

    if (!data.entry) {
      return { notFound: true };
    }

    return {
      props: {
        ...data,
      },
    };
  },
);
